import {
  styled,
  Box,
  AppBar,
  Tab,
  TabProps,
  Chip,
  Card,
  Button,
  tabsClasses,
  chipClasses,
} from '@mui/material';
import { TabList, TabPanel, TabPanelProps } from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AccessFeature from '~components/AccessFeature';
import { AccessFeatureCommonProps } from '~components/AccessFeature/types';

export const RoutesRoot2Container = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

export const RoutesAppBar = styled(AppBar)({
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  position: 'static',
});

export const RoutesTabList = styled(TabList)(({ theme }) => ({
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey['100'],

  [`& .${tabsClasses.indicator}`]: {
    backgroundColor: 'transparent',
  },
}));

export const RoutesTab = styled(
  ({
    validSubscriptionFeature,
    newActiveForVersion,
    className,
    ...restProps
  }: TabProps & {
    validSubscriptionFeature?: AccessFeatureCommonProps;
    newActiveForVersion?: string;
  }) => {
    if (validSubscriptionFeature) {
      return (
        <AccessFeature.Tab
          {...validSubscriptionFeature}
          {...restProps}
          newActiveForVersion={newActiveForVersion}
          className={className}
        />
      );
    }

    return <Tab {...restProps} className={className} />;
  },
)(({ theme }) => ({
  color: theme.palette.grey[600],
  minWidth: 180,
  borderRight: `1px solid ${theme.palette.grey.A200}`,

  '&:last-of-type': {
    borderRight: 'none',
  },

  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
  },
}));

export const RoutesTabsPanel2Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  backgroundColor: theme.palette.background.paper,
}));

export const RoutesTabPanel2 = styled(
  ({
    validSubscriptionFeature,
    className,
    ...restProps
  }: TabPanelProps & {
    validSubscriptionFeature?: AccessFeatureCommonProps;
  }) => {
    if (validSubscriptionFeature) {
      return (
        <AccessFeature.TabPanel
          {...validSubscriptionFeature}
          {...restProps}
          className={className}
        />
      );
    }

    return <TabPanel {...restProps} className={className} />;
  },
)({
  padding: 0,
  flex: 1,
});

export const RoutesTabPanelWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

export const RoutesTabPanelActions = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(3),
  justifyContent: 'space-between',

  [theme.breakpoints.down(1560)]: {
    flexDirection: 'column',
  },
}));

export const RoutesContentContainer = styled('div')(() => ({
  display: 'flex',
  width: 'calc(100vw - 94px)',
  height: '100%',
  minHeight: 680,
  maxHeight: 680,
}));

export const RoutesDataTableContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
  maxHeight: 'calc(100vh - 231.13px)',
  minHeight: 'calc(100vh - 231.13px)',
  width: 'calc(100vw - 94px)',
  overflowY: 'auto',

  '& .data-table-container': {
    display: 'flex',
    flexDirection: 'column',

    '& ._loading_overlay_wrapper': {
      height: 'auto',
      flex: 1,
    },
  },

  [theme.breakpoints.down(1560)]: {
    ...(!fullWidth && {
      width: 'calc(100vw - 694px)',
    }),
  },

  ...(!fullWidth && {
    width: 'calc(100vw - 994px)',
  }),
}));

/// NOT-DELETE old styles, they are reused in several parts
export const RoutesRootContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  flex: 1,
  gridTemplateRows: 'max-content max-content 1fr',
}));

export const RoutesActionFilterChip = styled(Chip)(({ theme }) => ({
  [`& .${chipClasses.label}`]: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: 80,
  },
  [`& .${chipClasses.icon}`]: {
    fontSize: theme.typography.pxToRem(14),
  },
}));

export const RoutesCardContent = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'defaultBackground',
})<{ defaultBackground?: boolean }>(({ theme, defaultBackground }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,

  ...(defaultBackground && {
    backgroundColor: theme.palette.background.default,
  }),
}));

export const RoutesTabsPanelContainer = styled(Box)({
  display: 'flex',
  flex: 1,
});

export const RoutesTabPanel = styled(TabPanel)({
  padding: 0,
  flex: 1,
});

export const RoutesIconButton = styled(Button)({
  minWidth: 0,
  padding: 12,
});

export const RoutesIconButtonIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(17),
}));
